import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { KeyRound, UserCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useBrand } from '../../context/BrandContext';

// The shell every settings section sits in: one heading, and a list of sections down the left.
//
// Each section is a real route rather than a tab held in state, so a section can be linked to, the
// back button works, and a refresh lands where the person was. That is the whole reason this is a
// layout and not a set of tabs.

const SECTIONS = [
  { label: 'Account', path: '/profile', Icon: UserCircle },
  {
    label: 'Figma plugin token',
    path: '/profile/figma-token',
    Icon: KeyRound,
    // Minting a token is the designer's job, not the reviewer's -- a different permission from
    // the one that opens Banner translations.
    permission: 'manage:figma_token',
    module: 'figma_loc',
  },
  {
    label: 'Customer.io token',
    path: '/profile/crm-token',
    Icon: KeyRound,
    // The token a CRM user pastes into the Customer.io workspace that calls the localization
    // endpoint. Same reasoning as the Figma token: a personal credential, gated by its own
    // permission.
    permission: 'manage:crm_token',
    module: 'crm_email',
  },
  {
    label: 'Configuration API token',
    path: '/profile/configuration-token',
    Icon: KeyRound,
    // The token a script uses against the public configuration endpoints. A personal credential
    // on Settings like the two above, but the permission is the Configuration page's own:
    // minting a token is the same power as setting values (the API re-checks it on every call).
    permission: 'manage:configuration',
  },
];

export default function ProfileLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const { activeBrandInfo } = useBrand();

  const brandModules = activeBrandInfo?.modules;

  // The same two gates the sidebar applies: the active brand must run the module, and the person
  // must hold the permission. Someone without both sees no mention of the section.
  const visible = SECTIONS.filter((section) => {
    if (section.module && brandModules && !brandModules.includes(section.module)) return false;
    return !section.permission || hasPermission(section.permission);
  });

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="mb-6">
        <h1 className="font-display text-xl tracking-tight text-ink">Settings</h1>
        <p className="text-sm text-ink-dim mt-1">Manage your account preferences.</p>
      </div>

      <div className="flex gap-8 items-start flex-col sm:flex-row">
        <nav className="w-full sm:w-52 shrink-0">
          <ul className="space-y-0.5">
            {visible.map(({ label, path, Icon }) => {
              const active = location.pathname === path;
              return (
                <li key={path}>
                  <button
                    type="button"
                    onClick={() => navigate(path)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold transition-colors ${
                      active
                        ? 'bg-surface-2 text-ink'
                        : 'text-ink-dim hover:text-ink hover:bg-surface-2/60'
                    }`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="flex-1 min-w-0 space-y-8">{children}</div>
      </div>
    </div>
  );
}
